/**
 * MarketplaceLanding.jsx
 * 
 * Customer-facing marketplace landing page.
 * Features:
 * - Hero search with city autocomplete dropdown
 * - Sticky search bar on scroll
 * - Explore Destinations (city cards grid) with skeleton loading
 * - Featured experiences carousel with skeleton loading
 * - "Book with Confidence" testimonials section
 * - Download app section with real App Store link
 */

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { SocialLinksLight } from '../components/SocialLinks';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Capacitor } from '@capacitor/core';
import { CapacitorHttp } from '@capacitor/core';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Search, 
  MapPin, 
  Star, 
  Clock, 
  Users, 
  ChevronRight,
  Waves,
  Mountain,
  Utensils,
  Palette,
  Heart,
  Moon,
  Leaf,
  BookOpen,
  ArrowRight,
  Globe,
  Smartphone,
  CheckCircle,
  Calendar,
  Shield,
  Zap,
  Quote,
  User,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { CATEGORY_OPTIONS } from '../constants/categories';
import { getCityImage } from '../constants/cityImages';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CustomerSignInModal from '../components/CustomerSignInModal';
import PickForMe from '../components/PickForMe';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Route service images through our proxy for WebP compression + resizing
// Reduces 2-3MB JPEGs to ~50-100KB WebP on the fly
const proxyImg = (url, w = 800, h = 600) => {
  if (!url) return '';
  return `${API_URL}/api/v2/public/image-proxy?url=${encodeURIComponent(url)}&w=${w}`;
};

// Skeleton component for city cards
const CityCardSkeleton = () => (
  <div 
    className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-200 animate-pulse"
    style={{ boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
  >
    <div className="absolute bottom-4 left-4 right-4">
      <div className="h-5 bg-gray-300 rounded w-24 mb-2" />
      <div className="h-3 bg-gray-300 rounded w-16" />
    </div>
  </div>
);

// Skeleton component for experience cards
const ExperienceCardSkeleton = () => (
  <Card 
    className="overflow-hidden rounded-2xl border-0 bg-white"
    style={{ boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
  >
    <div className="aspect-[4/3] bg-gray-200 animate-pulse" />
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-3 bg-gray-200 rounded w-16 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-12 animate-pulse" />
      </div>
      <div className="h-5 bg-gray-200 rounded w-full mb-2 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3 animate-pulse" />
      <div className="flex items-center gap-2 mb-3">
        <div className="h-4 bg-gray-200 rounded w-10 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
        <div className="h-5 bg-gray-200 rounded w-12 animate-pulse" />
      </div>
    </CardContent>
  </Card>
);

// Category icon mapping
const categoryIcons = {
  water_sports: Waves,
  waves: Waves,
  tours: MapPin,
  map: MapPin,
  food_drink: Utensils,
  utensils: Utensils,
  adventure: Mountain,
  mountain: Mountain,
  wellness: Heart,
  heart: Heart,
  arts_culture: Palette,
  palette: Palette,
  nature: Leaf,
  leaf: Leaf,
  nightlife: Moon,
  moon: Moon,
  classes: BookOpen,
  book: BookOpen,
  default: Globe
};

// Testimonials data
const testimonials = [
  {
    id: 1,
    text: "Booking was seamless! Found the perfect sunset cruise for our family reunion. The whole experience from search to checkout took less than 5 minutes.",
    author: "Sarah M.",
    location: "Austin, TX",
    rating: 5
  },
  {
    id: 2,
    text: "We had the best day ever. Captain Jake was incredible and the boat was spotless. Will definitely be booking again through BookingFlows!",
    author: "Michael R.",
    location: "Miami, FL",
    rating: 5
  },
  {
    id: 3,
    text: "So easy to compare options and prices. Found a great deal for my bachelorette party. Highly recommend!",
    author: "Jessica L.",
    location: "San Diego, CA",
    rating: 5
  }
];

const MarketplaceLanding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const [showStickySearch, setShowStickySearch] = useState(false);
  const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
  const [showPickForMe, setShowPickForMe] = useState(false);
  // Initialize with cached location to prevent double-fetch
  const [userLocation, setUserLocation] = useState(() => {
    try {
      const cached = sessionStorage.getItem('userLocation');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [locationSource, setLocationSource] = useState(() => {
    try {
      const cached = sessionStorage.getItem('userLocation');
      return cached ? JSON.parse(cached).source : null;
    } catch {
      return null;
    }
  });
  // Track if location resolution is complete (prevents double-fetch)
  const [locationResolved, setLocationResolved] = useState(() => {
    // If we have cached location, we're already resolved
    try {
      return !!sessionStorage.getItem('userLocation');
    } catch {
      return false;
    }
  });
  const searchRef = useRef(null);
  const heroRef = useRef(null);
  const headerSearchRef = useRef(null);

  // Component mounted - no verbose logging needed

  // Check if running in native app
  const isNative = Capacitor.isNativePlatform();

  // SWR fetcher with error handling - uses native HTTP on mobile
  const fetcher = async (url) => {
    try {
      let data;
      
      if (isNative) {
        // Use Capacitor's native HTTP for mobile apps - bypasses WebView CORS restrictions
        const response = await CapacitorHttp.get({
          url: url,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });
        if (response.status !== 200) {
          throw new Error('Failed to fetch');
        }
        data = response.data;
      } else {
        // Use regular fetch for web
        const res = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'omit'
        });
        if (!res.ok) {
          throw new Error('Failed to fetch');
        }
        data = await res.json();
      }
      
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Get location via client-side IP lookup (no permission needed!)
  useEffect(() => {
    // Skip if we already have location from useState initialization
    if (locationResolved) {
      return;
    }

    // Use client-side IP geolocation - NO PERMISSION NEEDED
    // This gives us approximate location (city-level) without asking
    const getLocationFromIP = async () => {
      // Add timeout to prevent long waits
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      try {
        // Try ipapi.co first (free, no API key needed, CORS enabled)
        const res = await fetch('https://ipapi.co/json/', { 
          mode: 'cors',
          cache: 'force-cache',
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (res.ok) {
          const data = await res.json();
          if (data.latitude && data.longitude) {
            const loc = {
              lat: data.latitude,
              lng: data.longitude,
              city: data.city,
              region: data.region,
              country: data.country_code,
              source: 'ip'
            };
            setUserLocation(loc);
            setLocationSource('ip');
            sessionStorage.setItem('userLocation', JSON.stringify(loc));
            setLocationResolved(true);
            return;
          }
        }
      } catch (e) {
        clearTimeout(timeoutId);
        // IP geolocation failed - will show global mix
      }
      
      // Fallback - show global mix (no location)
      setLocationResolved(true);
      setLocationSource('unknown');
    };

    getLocationFromIP();
  }, [locationResolved]);

  // Build location params - empty if location unknown (will show global mix)
  const locationParams = userLocation 
    ? `lat=${userLocation.lat}&lng=${userLocation.lng}` 
    : '';

  // Smart Cities - sorted by distance from user (or by popularity if no location)
  // Only fetch when location is resolved to prevent double-fetch
  const { data: citiesData, isLoading: citiesLoading } = useSWR(
    locationResolved ? `${API_URL}/api/marketplace/smart/cities?${locationParams}&limit=12` : null,
    fetcher,
    { 
      revalidateOnFocus: false,
      dedupingInterval: 60000,
      errorRetryCount: 2
    }
  );

  // ============= SMART LANDING - ONE CALL FOR ALL 3 SECTIONS =============
  const { data: landingData, isLoading: landingLoading } = useSWR(
    locationResolved ? `${API_URL}/api/marketplace/smart/landing?${locationParams}&featured_limit=4&discover_limit=4&trending_limit=4` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 30000, errorRetryCount: 2 }
  );

  // Categories (global, not location-based) - can fetch immediately
  const { data: categoriesData, isLoading: categoriesLoading } = useSWR(
    `${API_URL}/api/marketplace/categories`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000, errorRetryCount: 2 }
  );

  // Public Blogs for footer section
  const { data: blogsData } = useSWR(
    `${API_URL}/api/v2/blogs/public/blogs?limit=3`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000, errorRetryCount: 2 }
  );

  // Derive data from SWR - landing endpoint returns all 3 sections deduplicated
  const cities = citiesData?.cities || [];
  const categories = categoriesData?.categories || [];
  const publicBlogs = blogsData?.blogs || [];
  
  // All 3 sections from single landing call (backend handles deduplication)
  const featured = landingData?.featured?.services || [];
  const discover = landingData?.discover?.services || [];
  const trending = landingData?.trending?.services || [];
  const featuredIds = new Set(featured.map(s => s.id));
  
  // Show loading skeleton when location is resolving OR data is loading
  const loading = !locationResolved || citiesLoading || categoriesLoading || landingLoading;

  useEffect(() => {
    // Check if redirected with signin param
    if (searchParams.get('signin') === 'true') {
      setShowSignInModal(true);
    }
  }, [searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (headerSearchRef.current && !headerSearchRef.current.contains(e.target)) {
        setShowHeaderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect scroll to show sticky search bar using IntersectionObserver
  // This is more reliable than scroll events in various browser environments
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show sticky search when hero is less than 15% visible (triggers later)
        setShowStickySearch(!entry.isIntersecting || entry.intersectionRatio < 0.15);
      },
      {
        threshold: [0.15],
        rootMargin: '-64px 0px 0px 0px' // Account for fixed nav height
      }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCitySelect = (city) => {
    setShowDropdown(false);
    navigate(`/search?city=${encodeURIComponent(city.city)}`);
  };

  const handleCategoryClick = (category) => {
    navigate(`/search?category=${encodeURIComponent(category.name)}`);
  };

  const handleExperienceClick = (service) => {
    navigate(`/experience/${service.id}`);
  };

  const getCategoryIcon = (iconName) => {
    const Icon = categoryIcons[iconName?.toLowerCase()] || categoryIcons.default;
    return Icon;
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  // Filter cities based on search input
  const filteredCities = cities.filter(city => 
    city.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (city.country && city.country.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Filter cities for header search
  const headerFilteredCities = cities.filter(city => 
    city.city.toLowerCase().includes(headerSearchQuery.toLowerCase()) ||
    (city.country && city.country.toLowerCase().includes(headerSearchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Tours, Activities &amp; Experiences Worldwide | BookingFlows</title>
        <meta name="description" content="Browse and book tours, boat charters, outdoor adventures &amp; local experiences. Instant booking, secure payments, digital tickets. Find verified operators near you." />
        <meta property="og:title" content="Tours, Activities &amp; Experiences | BookingFlows" />
        <meta property="og:description" content="Book tours, charters &amp; experiences from verified operators. Instant confirmation, secure payment, digital tickets." />
        <link rel="canonical" href="https://bookingflows.com/" />
      </Helmet>
      {/* Navigation - with iOS safe area padding */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm shadow-gray-200/40"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: Always show logo header */}
          <div className="md:hidden flex items-center justify-between h-14">
            <Link to="/marketplace" className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-xl"
              />
            </Link>
            {isAuthenticated ? (
              <Link to="/my-trips">
                <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200">
                  {customer?.avatar_url ? (
                    <img src={customer.avatar_url} alt={customer.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                  )}
                </div>
              </Link>
            ) : (
              <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setShowSignInModal(true)}>
                Sign In
              </Button>
            )}
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between h-16 gap-4">
            <Link to="/marketplace" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-9 w-9 object-contain rounded-xl"
              />
              <span className="text-xl font-bold text-gray-900">BookingFlows</span>
            </Link>
            
            {/* Sticky Header Search Bar with Dropdown - Smooth transition */}
            <div 
              ref={headerSearchRef}
              className={`flex-1 max-w-xl relative transition-all duration-300 ease-out ${
                showStickySearch 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 -translate-y-3 pointer-events-none'
              }`}
            >
              <form onSubmit={(e) => {
                e.preventDefault();
                if (headerSearchQuery.trim()) {
                  navigate(`/search?q=${encodeURIComponent(headerSearchQuery)}`);
                }
              }}>
                <div className="flex items-center gap-2 p-1.5 bg-white rounded-full border border-gray-200">
                  <div className="flex-1 flex items-center gap-2 pl-3">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={headerSearchQuery}
                      onChange={(e) => {
                        setHeaderSearchQuery(e.target.value.slice(0, 100));
                        setShowHeaderDropdown(true);
                      }}
                      onFocus={() => setShowHeaderDropdown(true)}
                      placeholder="Where to?"
                      maxLength={100}
                      className="flex-1 border-0 outline-none text-sm placeholder:text-gray-400 bg-transparent"
                      data-testid="header-search-input"
                    />
                  </div>
                  <div className="hidden sm:flex items-center gap-1 px-2 text-xs text-gray-400 border-l border-gray-200">
                    <Globe className="w-3 h-3" />
                    <span>USA</span>
                  </div>
                  <Button 
                    type="submit"
                    size="sm"
                    className="rounded-full px-4 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* Header City Dropdown */}
              {showHeaderDropdown && (headerFilteredCities.length > 0 || cities.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[100]">
                  <div className="p-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {headerSearchQuery ? 'Matching Cities' : 'Popular Destinations'}
                    </p>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {(headerSearchQuery ? headerFilteredCities : cities).slice(0, 5).map((city) => (
                      <button
                        key={city.city}
                        onClick={() => {
                          setShowHeaderDropdown(false);
                          navigate(`/search?city=${encodeURIComponent(city.city)}`);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={getCityImage(city.city)}
                            alt={city.city}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{city.city}</p>
                          <p className="text-xs text-gray-500">{city.country}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">{city.operatorCount} {city.operatorCount === 1 ? 'provider' : 'providers'}</p>
                        </div>
                      </button>
                    ))}
                    {headerFilteredCities.length === 0 && headerSearchQuery && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <p>No destinations found for "{headerSearchQuery}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Look Up Booking — subtle grey text link, Sign In is the CTA */}
              <button
                onClick={() => navigate('/find-booking')}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors hidden sm:block"
                data-testid="look-up-booking-btn"
              >
                Look Up Booking
              </button>
              {isAuthenticated ? (
                <Link to="/my-trips">
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                    {customer?.avatar_url ? (
                      <img 
                        src={customer.avatar_url} 
                        alt={customer.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium text-gray-700 pr-1">
                      {customer?.name?.split(' ')[0] || 'My Trips'}
                    </span>
                  </div>
                </Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowSignInModal(true)}
                  data-testid="customer-signin-btn"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - padding accounts for fixed nav + iOS safe area */}
      <section ref={heroRef} className="relative pb-12 sm:pb-20 lg:pb-40 px-4" style={{ overflow: 'visible', paddingTop: 'calc(6rem + env(safe-area-inset-top, 0px))' }}>
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50 -z-10" />
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-4xl mx-auto text-center pt-12 sm:pt-16">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-5 leading-tight">
            Make it an<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Adventure Day
            </span>
          </h1>
          <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-10 max-w-2xl mx-auto px-4">
            Tours, Activities & Experiences Worldwide
          </p>

          {/* Main Search Bar with Dropdown */}
          <div ref={searchRef} className="max-w-2xl mx-auto relative z-40 px-2 sm:px-0">
            <form onSubmit={handleSearch}>
              <div className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 bg-white rounded-full shadow-xl shadow-gray-200/50 border border-gray-200">
                <div className="flex-1 flex items-center gap-2 sm:gap-3 pl-3 sm:pl-4">
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value.slice(0, 100));
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Where to?"
                    maxLength={100}
                    className="flex-1 border-0 outline-none text-sm sm:text-base placeholder:text-gray-400 bg-transparent"
                    data-testid="marketplace-search-input"
                  />
                </div>
                <div className="hidden sm:flex items-center gap-2 px-3 text-sm text-gray-400 border-l border-gray-200">
                  <Globe className="w-4 h-4" />
                  <span>USA</span>
                </div>
                <Button 
                  type="submit"
                  className="rounded-full px-4 sm:px-8 text-sm sm:text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="marketplace-search-btn"
                >
                  Search
                </Button>
              </div>
            </form>

            {/* City Dropdown - below header z-index */}
            {showDropdown && (filteredCities.length > 0 || cities.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 z-40">
                <div className="p-3 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {searchQuery ? 'Matching Cities' : 'Popular Destinations'}
                  </p>
                </div>
                <div className="max-h-[320px] overflow-y-auto">
                  {(searchQuery ? filteredCities : cities).slice(0, 6).map((city) => (
                    <button
                      key={city.city}
                      onClick={() => handleCitySelect(city)}
                      className="w-full flex items-center gap-4 p-3 hover:bg-gray-50 transition-colors text-left"
                      data-testid={`city-dropdown-${city.city}`}
                    >
                      <div className="w-14 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        <img
                          src={getCityImage(city.city)}
                          alt={city.city}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{city.city}</p>
                        <p className="text-sm text-gray-500">{city.country}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-400">{city.operatorCount} {city.operatorCount === 1 ? 'provider' : 'providers'}</p>
                      </div>
                    </button>
                  ))}
                  {filteredCities.length === 0 && searchQuery && (
                    <div className="p-4 text-center text-gray-500">
                      <p>No destinations found for {`"${searchQuery}"`}</p>
                      <p className="text-sm mt-1">Try searching for a city name</p>
                    </div>
                  )}
                  {cities.length === 0 && !searchQuery && (
                    <div className="p-4 text-center text-gray-500">
                      <p>No destinations available yet</p>
                      <p className="text-sm mt-1">Check back soon!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cheeky disclaimer + Pick for me - collapses when dropdown is open */}
            <div className={`overflow-hidden transition-all duration-200 ${showDropdown ? 'max-h-0 mt-0' : 'max-h-20 mt-4'}`}>
              {!locationResolved ? (
                <p className="text-center text-sm text-blue-600 animate-pulse">
                  Finding experiences near you...
                </p>
              ) : (
                <p className="text-center text-sm text-gray-500">
                  By continuing you agree to having a great adventure. 
                  <button
                    onClick={() => setShowPickForMe(true)}
                    className="text-blue-600 hover:text-blue-700 transition-colors ml-1"
                    data-testid="pick-for-me-btn"
                  >
                    Can&apos;t decide? Let us pick for you
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ⭐ Featured Experiences - Top Performers */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                <Star className="inline w-6 h-6 text-amber-500 mr-2 -mt-1" />
                Featured Experiences
              </h2>
              <p className="text-gray-600 mt-1">
                {/* Only say "near city" if we actually have services in that city */}
                {featured.length > 0 && featured.some(s => s.distance && s.distance < 100) && userLocation?.city
                  ? `Top-rated near ${userLocation.city}`
                  : 'Top-rated from trusted operators'
                }
              </p>
            </div>
            <Button 
              variant="ghost" 
              className="hidden sm:flex gap-1"
              onClick={() => navigate('/search')}
            >
              View all <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {loading ? (
              <>
                {[...Array(4)].map((_, i) => (
                  <ExperienceCardSkeleton key={i} />
                ))}
              </>
            ) : featured.length > 0 ? (
              featured.map((service, serviceIdx) => (
                <Card 
                  key={service.id}
                  className="group cursor-pointer overflow-hidden rounded-2xl border-0 bg-white transition-all duration-300"
                  style={{ 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }}
                  onClick={() => handleExperienceClick(service)}
                  data-testid={`featured-card-${service.id}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    {service.mainImage || (service.images && service.images[0]) ? (
                      <>
                        <img
                          src={proxyImg(service.mainImage || service.images?.[0])}
                          alt={service.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading={serviceIdx === 0 ? 'eager' : 'lazy'}
                          fetchPriority={serviceIdx === 0 ? 'high' : 'auto'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center absolute inset-0">
                          <Star className="w-12 h-12 text-amber-300" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                        <Star className="w-12 h-12 text-amber-300" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-amber-400 text-amber-900 text-xs font-semibold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Featured
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{service.city || 'Location TBD'}</span>
                        {service.distance && service.distance < 100 && (
                          <>
                            <span className="mx-1">·</span>
                            <span className="text-green-600">{Math.round(service.distance)} mi</span>
                          </>
                        )}
                      </div>
                      {service.operator?.rating > 0 && (
                        <span className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const rating = service.operator.rating || 0;
                            const roundedRating = Math.ceil(rating * 2) / 2;
                            const filled = star <= Math.floor(roundedRating);
                            return (
                              <Star 
                                key={star}
                                className={`w-3 h-3 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                              />
                            );
                          })}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-amber-600 transition-colors">
                      {service.name}
                    </h3>
                    {service.operator && (
                      <p className="text-sm text-gray-500 mb-3">
                        by {service.operator.name}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(service.durationMinutes)}
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-amber-600">
                          {formatPrice(service.price, service.currency)}
                        </span>
                        {service.priceType === 'per_person' && (
                          <span className="text-xs text-gray-500">/person</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <>
                {[...Array(4)].map((_, i) => (
                  <ExperienceCardSkeleton key={i} />
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* 🎯 Discover Experiences - Mixed Categories */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Discover Experiences</h2>
              <p className="text-gray-600 mt-1">A mix of activities you might enjoy</p>
            </div>
            <Button 
              variant="ghost" 
              className="hidden sm:flex gap-1"
              onClick={() => navigate('/search')}
            >
              View all <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {loading ? (
              <>
                {[...Array(4)].map((_, i) => (
                  <ExperienceCardSkeleton key={i} />
                ))}
              </>
            ) : discover.length > 0 ? (
              discover.map((service) => (
                <Card 
                  key={service.id}
                  className="group cursor-pointer overflow-hidden rounded-2xl border-0 bg-white transition-all duration-300"
                  style={{ 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }}
                  onClick={() => handleExperienceClick(service)}
                  data-testid={`discover-card-${service.id}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    {service.mainImage || (service.images && service.images[0]) ? (
                      <>
                        <img
                          src={proxyImg(service.mainImage || service.images[0])}
                          alt={service.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center absolute inset-0">
                          <Globe className="w-12 h-12 text-blue-300" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                        <Globe className="w-12 h-12 text-blue-300" />
                      </div>
                    )}
                    {service.category && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full capitalize">
                          {service.category.replace('-', ' ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{service.city || 'Location TBD'}</span>
                      </div>
                      {service.operator?.rating > 0 && (
                        <span className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const rating = service.operator.rating || 0;
                            const roundedRating = Math.ceil(rating * 2) / 2;
                            const filled = star <= Math.floor(roundedRating);
                            return (
                              <Star 
                                key={star}
                                className={`w-3 h-3 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                              />
                            );
                          })}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                      {service.name}
                    </h3>
                    {service.operator && (
                      <p className="text-sm text-gray-500 mb-3">
                        by {service.operator.name}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(service.durationMinutes)}
                      </span>
                      <div className="text-right">
                        <span className="font-bold" style={{ color: '#2563eb' }}>
                          {formatPrice(service.price, service.currency)}
                        </span>
                        {service.priceType === 'per_person' && (
                          <span className="text-xs text-gray-500">/person</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full py-8 text-center text-gray-500">
                <p>More experiences coming soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 🏙️ Explore Destinations - Cities Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Explore Destinations</h2>
              <p className="text-gray-600 mt-1">
                {/* Only mention "near you" if we have cities that are actually nearby */}
                {cities.length > 0 && cities.some(c => c.distance && c.distance < 200) && userLocation?.city
                  ? `Popular destinations including cities near you`
                  : 'Find experiences in these cities'
                }
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {loading ? (
              <>
                {[...Array(4)].map((_, i) => (
                  <CityCardSkeleton key={i} />
                ))}
              </>
            ) : cities.length > 0 ? (
              cities.slice(0, 4).map((city) => (
                <button
                  key={city.city}
                  onClick={() => handleCitySelect(city)}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden transition-all duration-300"
                  style={{ 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }}
                  data-testid={`city-card-${city.city}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <img
                    src={getCityImage(city.city)}
                    alt={city.city}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg">{city.city}</h3>
                    <div className="flex items-center justify-between">
                      <p className="text-white/80 text-sm">
                        {city.serviceCount || city.operatorCount} {(city.serviceCount || city.operatorCount) === 1 ? 'experience' : 'experiences'}
                      </p>
                      {city.distance && city.distance < 500 && (
                        <p className="text-white/60 text-xs">
                          {Math.round(city.distance)} mi
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <>
                {[...Array(4)].map((_, i) => (
                  <CityCardSkeleton key={i} />
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* 📈 Trending Now - Recent Bookings */}
      {trending.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  <TrendingUp className="inline w-6 h-6 text-blue-500 mr-2 -mt-1" />
                  Trending Now
                </h2>
                <p className="text-gray-600 mt-1">Popular experiences people are booking</p>
              </div>
              <Button 
                variant="ghost" 
                className="hidden sm:flex gap-1"
                onClick={() => navigate('/search')}
              >
                View all <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {trending.map((service) => (
                <Card 
                  key={service.id}
                  className="group cursor-pointer overflow-hidden rounded-2xl border-0 bg-white transition-all duration-300"
                  style={{ 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }}
                  onClick={() => handleExperienceClick(service)}
                  data-testid={`trending-card-${service.id}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    {service.mainImage || (service.images && service.images[0]) ? (
                      <>
                        <img
                          src={proxyImg(service.mainImage || service.images[0])}
                          alt={service.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center absolute inset-0">
                          <TrendingUp className="w-12 h-12 text-blue-300" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                        <TrendingUp className="w-12 h-12 text-blue-300" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{service.city || 'Location TBD'}</span>
                      </div>
                      {service.operator?.rating > 0 && (
                        <span className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const rating = service.operator.rating || 0;
                            const roundedRating = Math.ceil(rating * 2) / 2;
                            const filled = star <= Math.floor(roundedRating);
                            return (
                              <Star 
                                key={star}
                                className={`w-3 h-3 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                              />
                            );
                          })}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                      {service.name}
                    </h3>
                    {service.operator && (
                      <p className="text-sm text-gray-500 mb-3">
                        by {service.operator.name}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(service.durationMinutes)}
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-blue-600">
                          {formatPrice(service.price, service.currency)}
                        </span>
                        {service.priceType === 'per_person' && (
                          <span className="text-xs text-gray-500">/person</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Booking Made Simple - How it works */}
      <section className="py-16 px-4 overflow-hidden relative">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-blue-50/30" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Booking Made Simple
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From browse to booked in just a few clicks. No hidden fees, no hassle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 - Browse */}
            <div className="relative group text-center">
              <div className="relative rounded-2xl overflow-hidden mb-5 aspect-[4/5] max-w-[286px] mx-auto"
                style={{ boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1758275557161-f117d724d769?w=400&h=500&fit=crop"
                  alt="Friends with colorful powder celebrating"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  style={{ filter: 'brightness(1.1) saturate(1.2)' }}
                />
              </div>
              <div className="px-2">
                <div className="inline-flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">1</span>
                  <h3 className="font-bold text-gray-900 text-lg">Browse</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Explore thousands of experiences, filter by location, price, and availability
                </p>
              </div>
            </div>

            {/* Step 2 - Choose */}
            <div className="relative group text-center">
              <div className="relative rounded-2xl overflow-hidden mb-5 aspect-[4/5] max-w-[286px] mx-auto"
                style={{ boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1746138762885-5c0161f0c4d3?w=400&h=500&fit=crop"
                  alt="Yellow kayak on turquoise water"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  style={{ filter: 'brightness(1.1) saturate(1.15)' }}
                />
              </div>
              <div className="px-2">
                <div className="inline-flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">2</span>
                  <h3 className="font-bold text-gray-900 text-lg">Choose</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Select your perfect date, time, and group size with real-time availability
                </p>
              </div>
            </div>

            {/* Step 3 - Confirm */}
            <div className="relative group text-center">
              <div className="relative rounded-2xl overflow-hidden mb-5 aspect-[4/5] max-w-[286px] mx-auto"
                style={{ boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1694568353517-b72c2ffafa6b?w=350&h=440&fit=crop"
                  alt="Colorful hot air balloon in blue sky"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  style={{ filter: 'brightness(1.1) saturate(1.2)' }}
                />
              </div>
              <div className="px-2">
                <div className="inline-flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">3</span>
                  <h3 className="font-bold text-gray-900 text-lg">Confirm</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Instant booking confirmation and tickets delivered straight to your phone
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Things To Do - Visual Category Cards */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Things To Do</h2>
              <p className="text-gray-600 mt-1">Discover experiences by category</p>
            </div>
            <Link to="/search" aria-label="View all experiences">
              <Button variant="ghost" className="hidden sm:flex gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {CATEGORY_OPTIONS.map((category) => (
              <Link
                key={category.id}
                to={`/search?category=${encodeURIComponent(category.id)}`}
                className="group relative aspect-[3/2] rounded-xl overflow-hidden"
                style={{ 
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                }}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                  <span className="text-lg">{category.icon}</span>
                  <span className="text-white font-medium text-sm">{category.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Book with Confidence - Testimonials Section (GetMyBoat style) */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Book with Confidence</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real reviews from happy adventurers who found their perfect experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="bg-white rounded-2xl p-6 border-0 transition-all duration-300"
                style={{ 
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-blue-100 mb-2" />
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {testimonial.author.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium">Secure Payments</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium">Verified Operators</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Zap className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Instant Confirmation</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              <span className="text-sm font-medium">4.9 Average Rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* Ironic, Right? - Our Mission Section */}
      <section className="py-20 px-4 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Ironic, <span className="text-blue-600">right?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We're using technology to get away from technology. But that's kind of the point.
              </p>
              <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  AI can write emails, generate images, even hold conversations.
                  But it can't feel the spray from a jet ski in <span className="text-blue-600 font-medium">Miami</span>. 
                  Can't taste street tacos in <span className="text-blue-600 font-medium">Mexico City</span>.
                  Can't laugh with friends on a sunset cruise in <span className="text-blue-600 font-medium">Dubai</span>.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We built BookingFlows so you can spend less time planning
                  and more time <span className="text-blue-600 font-semibold">living</span>.
                </p>
              </div>
            </div>

            {/* Right - Angled Photos */}
            <div className="relative h-[400px] lg:h-[500px] hidden sm:block">
              {/* Photo 1 - Jet Ski (Miami) */}
              <div 
                className="absolute w-48 h-64 rounded-2xl overflow-hidden"
                style={{ 
                  left: '0%', 
                  top: '18%', 
                  transform: 'rotate(-8deg)',
                  boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)'
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_441dc0f8-02fe-432d-8d97-4769bd9c9537/artifacts/n4cpi7tw_istockphoto-98462741-612x612.jpg"
                  alt="Jet ski on blue ocean"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Photo 2 - Street Tacos (Mexico City) */}
              <div 
                className="absolute w-56 h-72 rounded-2xl overflow-hidden z-10"
                style={{ 
                  left: '28%', 
                  top: '22%', 
                  transform: 'rotate(4deg)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)'
                }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1768716697797-35dffd4b27f7?w=350&h=450&fit=crop"
                  alt="Authentic street tacos in Mexico City"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Photo 3 - Dubai Sunset Yacht */}
              <div 
                className="absolute w-44 h-56 rounded-2xl overflow-hidden"
                style={{ 
                  right: '2%', 
                  top: '8%', 
                  transform: 'rotate(10deg)',
                  boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)'
                }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1768463852076-8317da3d58ca?w=300&h=380&fit=crop"
                  alt="Sunset yacht cruise in Dubai"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section - With real App Store link */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div className="text-white">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Take BookingFlows with you
                </h2>
                <p className="text-blue-100 mb-6">
                  Download our app and book experiences on the go. Get exclusive mobile-only deals and instant notifications.
                </p>
                <div className="flex flex-wrap gap-4">
                  {/* App Store Button - Real link */}
                  <a 
                    href="https://apps.apple.com/us/app/bookingflows/id6757174499"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-black hover:bg-gray-900 px-5 py-3 rounded-xl transition-colors"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Download on the</p>
                      <p className="text-sm font-semibold">App Store</p>
                    </div>
                  </a>
                  
                  {/* Play Store Button - Coming Soon */}
                  <div className="flex items-center gap-3 bg-black/40 px-5 py-3 rounded-xl cursor-not-allowed opacity-75">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Coming Soon</p>
                      <p className="text-sm font-semibold">Google Play</p>
                    </div>
                  </div>
                </div>
                
                {/* How it works - under app section */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-sm font-medium text-white mb-4">How it works</p>
                  <div className="flex flex-col gap-3 text-sm text-blue-100">
                    <span className="flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Browse thousands of experiences worldwide
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Pick your date and group size
                    </span>
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Book instantly - you're all set!
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Phone mockup */}
              <div className="hidden md:flex justify-center">
                <div className="relative">
                  <div className="w-64 h-[500px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex items-center justify-center">
                      <div className="text-center p-6">
                        <img 
                          src="/logo-icon.png" 
                          alt="BookingFlows" 
                          className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg"
                        />
                        <p className="font-bold text-gray-900 text-lg">BookingFlows</p>
                        <p className="text-gray-500 text-sm mt-1">Your adventure awaits</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Empty State */}
      {!loading && cities.length === 0 && featured.length === 0 && (
        <section className="py-24 px-4 text-center">
          <Globe className="w-16 h-16 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon</h2>
          <p className="text-gray-600 max-w-md mx-auto mb-8">
            We&apos;re working with amazing local operators to bring you unforgettable experiences. Check back soon!
          </p>
          <Link to="/for-suppliers">
            <Button className="gap-2">
              Become an operator <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </section>
      )}

      {/* Business Stories section removed - Blog link moved to footer */}

      {/* ═══ SEO: Things to Do by City ═══════════════════════════════════ */}
      <section className="py-16 px-4 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Find Experiences Near You
          </h2>
          <p className="text-gray-500 mb-8">Explore tours, charters, and activities in top US cities</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { slug: 'austin-tx', label: 'Austin, TX' },
              { slug: 'miami-fl', label: 'Miami, FL' },
              { slug: 'new-york-city-ny', label: 'New York City, NY' },
              { slug: 'los-angeles-ca', label: 'Los Angeles, CA' },
              { slug: 'chicago-il', label: 'Chicago, IL' },
              { slug: 'denver-co', label: 'Denver, CO' },
              { slug: 'nashville-tn', label: 'Nashville, TN' },
              { slug: 'key-west-fl', label: 'Key West, FL' },
              { slug: 'san-francisco-ca', label: 'San Francisco, CA' },
              { slug: 'seattle-wa', label: 'Seattle, WA' },
              { slug: 'new-orleans-la', label: 'New Orleans, LA' },
              { slug: 'orlando-fl', label: 'Orlando, FL' },
              { slug: 'las-vegas-nv', label: 'Las Vegas, NV' },
              { slug: 'san-diego-ca', label: 'San Diego, CA' },
              { slug: 'boston-ma', label: 'Boston, MA' },
              { slug: 'honolulu-hi', label: 'Honolulu, HI' },
              { slug: 'scottsdale-az', label: 'Scottsdale, AZ' },
              { slug: 'asheville-nc', label: 'Asheville, NC' },
              { slug: 'charleston-sc', label: 'Charleston, SC' },
              { slug: 'savannah-ga', label: 'Savannah, GA' },
              { slug: 'sedona-az', label: 'Sedona, AZ' },
              { slug: 'jackson-hole-wy', label: 'Jackson Hole, WY' },
              { slug: 'aspen-co', label: 'Aspen, CO' },
              { slug: 'napa-ca', label: 'Napa, CA' },
              { slug: 'portland-or', label: 'Portland, OR' },
              { slug: 'phoenix-az', label: 'Phoenix, AZ' },
              { slug: 'houston-tx', label: 'Houston, TX' },
              { slug: 'dallas-tx', label: 'Dallas, TX' },
              { slug: 'san-antonio-tx', label: 'San Antonio, TX' },
              { slug: 'galveston-tx', label: 'Galveston, TX' },
              { slug: 'tampa-fl', label: 'Tampa, FL' },
              { slug: 'fort-lauderdale-fl', label: 'Fort Lauderdale, FL' },
              { slug: 'myrtle-beach-sc', label: 'Myrtle Beach, SC' },
              { slug: 'outer-banks-nc', label: 'Outer Banks, NC' },
              { slug: 'maui-hi', label: 'Maui, HI' },
              { slug: 'cape-cod-ma', label: 'Cape Cod, MA' },
              { slug: 'boulder-co', label: 'Boulder, CO' },
              { slug: 'minneapolis-mn', label: 'Minneapolis, MN' },
              { slug: 'atlanta-ga', label: 'Atlanta, GA' },
              { slug: 'philadelphia-pa', label: 'Philadelphia, PA' },
            ].map(({ slug, label }) => (
              <Link
                key={slug}
                to={`/tours-in/${slug}`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 text-sm transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="line-clamp-1">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SEO: Booking Software for Businesses ═══════════════════════ */}
      <section className="py-16 px-4 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Booking Software for Tour Operators, Charters &amp; Rentals
          </h2>
          <div className="grid md:grid-cols-2 gap-8 text-gray-600 text-sm leading-relaxed">
            <div className="space-y-3">
              <p>
                <strong className="text-gray-900">BookingFlows</strong> is online booking software built for experience-based businesses — tour operators, boat charter companies, rental outfitters, museums, escape rooms, music venues, and any business that takes reservations.
              </p>
              <p>
                Embed a booking widget on your website in minutes. Customers can book 24/7, pay securely via Stripe, and receive a digital ticket instantly. No phone tag, no manual invoices.
              </p>
              <p>
                Manage your entire team from one dashboard. Assign guides, track assets, collect digital waivers, and send automated confirmation emails — all included at no extra cost.
              </p>
            </div>
            <div className="space-y-3">
              <p>
                Unlike competitors charging $49–$299/month, BookingFlows is <strong className="text-gray-900">free to start</strong>. We charge a small platform fee at checkout — paid by your customers, not you. You keep more of every booking.
              </p>
              <p>
                Purpose-built for <Link to="/for-tours" className="text-blue-600 hover:underline font-medium">tour booking software</Link>, <Link to="/for-charters" className="text-blue-600 hover:underline font-medium">charter booking software</Link>, and <Link to="/for-rentals" className="text-blue-600 hover:underline font-medium">rental booking software</Link>. Powerful enough for enterprise, simple enough for solo operators.
              </p>
              <p>
                <Link to="/for-suppliers" className="text-blue-600 hover:underline font-medium">Start accepting online bookings today →</Link>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/for-suppliers" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium">
              Booking Software — Free to Start
            </Link>
            <Link to="/for-tours" className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors">
              Tour Operator Software
            </Link>
            <Link to="/for-charters" className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors">
              Charter Booking Software
            </Link>
            <Link to="/for-rentals" className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors">
              Rental Booking Software
            </Link>
          </div>
        </div>
      </section>

      {/* Operator Sign-Up CTA Section */}
      <section className="bg-gray-950 py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
            Booking Software
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Run your business<br />on BookingFlows
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
            Join tour operators, rental companies, and experience providers who take bookings online, get paid automatically, and manage their team—all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/for-suppliers">
              <Button
                data-testid="marketplace-signup-cta"
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-base px-8 py-4 h-auto rounded-xl transition-all duration-200"
              >
                Sign up free <ArrowRight className="ml-2 w-4 h-4 inline-block" />
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 hover:border-white/40 font-medium text-base px-8 py-4 h-auto rounded-xl transition-all duration-200"
              >
                Log in to your account
              </Button>
            </Link>
          </div>
          <p className="text-gray-600 text-sm mt-6">Free setup · No commitment required</p>
        </div>
      </section>

      {/* Footer - White & Friendly */}
      <footer className="py-12 px-4 bg-white border-t border-gray-100 pb-[calc(3rem+env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
            {/* Explore */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Explore</h3>
              <ul className="space-y-2">
                <li><Link to="/search" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">All Experiences</Link></li>
                <li><Link to="/search?category=water" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Water Activities</Link></li>
                <li><Link to="/search?category=tours" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Guided Tours</Link></li>
                <li><Link to="/search?category=outdoor" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Outdoor Adventures</Link></li>
                <li><Link to="/find-booking" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Look Up Booking</Link></li>
              </ul>
            </div>
            {/* Top Cities */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Top Cities</h4>
              <ul className="space-y-2">
                <li><Link to="/tours-in/austin-tx" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Things to do in Austin</Link></li>
                <li><Link to="/tours-in/miami-fl" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Things to do in Miami</Link></li>
                <li><Link to="/tours-in/new-york-city-ny" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Things to do in New York</Link></li>
                <li><Link to="/tours-in/nashville-tn" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Things to do in Nashville</Link></li>
                <li><Link to="/tours-in/denver-co" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Things to do in Denver</Link></li>
                <li><Link to="/tours-in/key-west-fl" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Things to do in Key West</Link></li>
              </ul>
            </div>
            {/* More Cities */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">More Cities</h4>
              <ul className="space-y-2">
                <li><Link to="/tours-in/san-francisco-ca" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">San Francisco</Link></li>
                <li><Link to="/tours-in/los-angeles-ca" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Los Angeles</Link></li>
                <li><Link to="/tours-in/chicago-il" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Chicago</Link></li>
                <li><Link to="/tours-in/new-orleans-la" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">New Orleans</Link></li>
                <li><Link to="/tours-in/seattle-wa" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Seattle</Link></li>
                <li><Link to="/tours-in/charleston-sc" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Charleston</Link></li>
              </ul>
            </div>
            {/* Software */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Software</h4>
              <ul className="space-y-2">
                <li><Link to="/for-suppliers" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Booking Software</Link></li>
                <li><Link to="/for-charters" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Charter Software</Link></li>
                <li><Link to="/for-tours" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Tour Software</Link></li>
              <li><Link to="/for-rentals" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Rental Software</Link></li>
                <li><Link to="/customers/boatx" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Customer Stories</Link></li>
                <li><Link to="/login" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Operator Login</Link></li>
                <li><Link to="/signup" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Get Started Free</Link></li>
              </ul>
            </div>
            {/* Support */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/support" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Contact Us</Link></li>
                <li><Link to="/blog" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Blog</Link></li>
                <li><Link to="/find-booking" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Find My Booking</Link></li>
              </ul>
            </div>
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-600 hover:text-blue-600 text-sm transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-xl"
              />
              <span className="text-lg font-bold text-gray-900">BookingFlows</span>
            </div>
            <SocialLinksLight />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} BookingFlows. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Customer Sign In Modal */}
      <CustomerSignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal} 
      />

      {/* Pick For Me Modal */}
      <PickForMe 
        open={showPickForMe} 
        onOpenChange={setShowPickForMe} 
      />

      {/* Bottom fade effect for smooth scrolling - respects safe area */}
      <div 
        className="fixed left-0 right-0 h-16 pointer-events-none z-40"
        style={{
          bottom: 'env(safe-area-inset-bottom, 0px)',
          background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
      {/* Safe area bottom fill */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white pointer-events-none z-40"
        style={{
          height: 'env(safe-area-inset-bottom, 0px)'
        }}
      />
    </div>
  );
};

export default MarketplaceLanding;
